<!-- eslint-disable max-len -->
<template>
  <v-card>
    <slot name="title">
      <v-card-title
        class="text-body-1 justify-space-between"
        @click="$emit('click:expand', !expanded)"
      >
        <span>{{ title }}</span>
        <v-spacer />
        <slot name="badge">
          <template v-if="typeof totalBadge === 'number'">
            <v-skeleton-loader
              class="searchbox-badge-skeleton"
              v-if="loading"
              type="avatar"
            />
            <v-badge
              v-else
              class="d-inline"
              bordered
              color="#27846d"
              :content="`${totalBadge}`"
              inline
            />
          </template>
        </slot>
      </v-card-title>
    </slot>
    <v-expand-transition>
      <div v-show="expanded">
        <slot name="filters" />
        <v-divider></v-divider>
        <v-simple-table v-if="!loading" :style="{
            'overflow': overflow ? 'auto' : undefined,
            'maxHeight': overflow ? `${loaderRows * 48}px` : undefined, // 48 = height per row
          }">
          <template v-slot:default>
            <thead v-if="items.length">
              <tr>
                <th
                  v-for="(label, idx) in labels"
                  :key="`label-${idx}`"
                  @click="$emit('click:header', { index: idx, label })"
                >
                  <slot name="label">
                    <slot :name="`label-${idx}`">
                      {{ label }}
                    </slot>
                  </slot>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in items" :key="`row-${i}`">
                <td
                  v-for="(column, j) in (notTooltip() ? row : row.slice(0, 2))"
                  :key="`column-${j}`"
                  @click="$emit('click:item', { index: i, row, column })"
                  :class="{
                    'c-pointer': true,
                    'sizeIcon': (isObject(column) && requestRow(row)),
                    'sizeText': (!isObject(column) && requestRow(row)),
                    'td__icon': (isObject(column)),
                  }"
                >
                  <v-tooltip
                    :disabled="true"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <div v-on="on" v-bind="attrs">
                        <slot
                          name="item"
                          v-bind:idx="i"
                          v-bind:column="column"
                          v-bind:row="row"
                        >
                          <v-avatar
                            class="gradient-button"
                            size="24"
                            v-if="
                              column &&
                              typeof column === 'string' &&
                              column.startsWith('mdi-')
                            "
                          >
                            <v-icon
                              class="primary white--text"
                              v-text="column"
                              small
                            ></v-icon>
                          </v-avatar>

                          <slot
                            v-else
                            :name="`item-${j}`"
                            v-bind:idx="j"
                            v-bind:column="column"
                            v-bind:row="row"
                          >
                              <v-tooltip
                                :key="items[i][2]"
                                transition="fade-transition"
                                :disabled="notTooltip()"
                                bottom
                              >
                              <template v-slot:activator="{ on, attrs }">
                                <span v-on="on" v-bind="attrs">{{ column }}</span>
                              </template>
                              <span> {{items[i][2]}} </span>
                            </v-tooltip>
                          </slot>
                        </slot>
                      </div>
                    </template>
                  </v-tooltip>
                </td>
              </tr>
              <tr v-if="items.length === 0 && isFormGeneric">
                <td :colspan="colspan" class="text-center">
                  <slot name="emptyMessage">
                    {{ $t("components.searchBox.formGeneric") }}
                  </slot>
                </td>
              </tr>
              <tr v-if="items.length === 0 && !isFormGeneric">
                <td :colspan="colspan" class="text-center">
                  <slot v-if="isPending" name="emptyMessage">
                    {{ $t("views.home.pendingRequests.emty") }}
                  </slot>
                  <slot v-else name="emptyMessage">
                    {{ $t("views.home.myRequests.emty") }}
                  </slot>
                </td>
              </tr>
              <!-- <tr v-if="viewMoreLink">
                <td :colspan="colspan" class="text-center">
                  <slot name="viewMore">
                    <v-btn
                      @click="$emit('click:viewMore')"
                      color="primary"
                      text
                    >
                      {{ $t('components.searchBox.viewMore') }}
                    </v-btn>
                  </slot>
                </td>
              </tr> -->
            </tbody>
          </template>
        </v-simple-table>

        <div v-if="!loading" class="text-center">
          <slot name="viewMore" v-if="viewMoreLink">
            <v-divider />
            <v-btn
              class="my-1"
              @click="$emit('click:viewMore')"
              color="primary"
              text
            >
              {{ $t("components.searchBox.list") }}
            </v-btn>
          </slot>
        </div>

        <template v-else>
          <v-skeleton-loader
            v-for="idx in loaderRows"
            :key="idx"
            :type="`list-item`"
            v-bind:class="[
              'px-4',
              `idx-${idx}`,
              {
                'mt-1 pt-4': idx === 1,
                'pb-4': idx === loaderRows,
              },
            ]"
          />
          <v-skeleton-loader class="my-1" type="divider" />

          <div class="pa-2">
            <v-skeleton-loader class="mx-auto" max-width="80px" type="text" />
          </div>
        </template>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>

export default {
  props: {
    title: String,
    totalBadge: [Number, String],
    viewMoreLink: {
      type: Boolean,
      default: () => true,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    expanded: {
      type: Boolean,
      default: () => true,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    loaderRows: {
      type: Number,
      default: () => 5,
    },
    overflow: {
      type: Boolean,
      default: () => false,
    },
    isFormGeneric: {
      type: Boolean,
      default: () => false,
    },
    isPending: {
      type: Boolean,
      default: () => false,
    },
  },
  computed: {
    colspan() {
      return this.labels.length || 1;
    },
  },
  methods: {
    notTooltip() {
      return this.title === this.$t('views.home.pendingRequests.title') || this.title === this.$t('views.home.myRequests.title');
    },
    redirectToFormGeneralClick() {
      this.$root.$emit('openFormGeneric');
    },
    isObject(column) {
      return typeof column === 'object';
    },
    requestRow(row) {
      return row.length === 3;
    },
  },
};
</script>

<style scoped>
.c-pointer {
  cursor: pointer;
}
.iconColumn__mdieye {
  width: 10px;
  padding: 0 !important;
}

.tdCenterIcon {
  padding-top: 0.3rem !important;
}
.td__icon {
  padding-right: 0 !important;
}
.sizeText{
  padding-right: 0px !important;
}
@media screen and (max-width: 600px) {
    .sizeIcon{
      padding-right: 0px !important;
    }
    .sizeText{
      padding-left: 0px !important;
    }
  }

</style>

<style>
.searchbox-badge-skeleton .v-skeleton-loader__avatar {
  width: 24px;
  height: 24px;
}
.icon__interesado--searchBox {
  font-size: 2.5rem !important;
}
.icon__seguidor--searchBox {
  font-size: 2rem !important;
  margin-left: 0.2rem;
}

</style>
